import React, { useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const formatNumber = (n) => Number(n || 0).toLocaleString("pt-BR");

/**
 * Lançamento manual de créditos avulsos de campanha numa empresa (super admin).
 *
 * O saldo nunca é editado como campo do cadastro da empresa: toda alteração
 * passa por aqui, que grava um movimento na razão de créditos. Assim o extrato
 * sempre explica o saldo. Valor negativo serve para corrigir um lançamento
 * errado; o backend não deixa o saldo ficar abaixo de zero.
 */
const CampaignCreditGrantDialog = ({ open, company, currentBalance, onClose, onGranted }) => {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount("");
      setDescription("");
    }
  }, [open]);

  const parsed = parseInt(amount, 10);
  const valid = Number.isFinite(parsed) && parsed !== 0;
  const preview =
    valid && currentBalance != null ? Math.max(currentBalance + parsed, 0) : null;

  const handleSave = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post("/campaigns/credits", {
        companyId: company.id,
        amount: parsed,
        description: description.trim() || undefined,
      });
      toast.success(
        `Créditos lançados. Saldo avulso de ${company.name}: ${formatNumber(data.balanceAfter)}.`
      );
      onGranted(data.balanceAfter);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  if (!company) return null;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Créditos de campanha</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" gutterBottom>
          <strong>{company.name}</strong>
        </Typography>
        {currentBalance != null && (
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Saldo avulso atual: {formatNumber(currentBalance)}
          </Typography>
        )}

        <Box mt={2}>
          <TextField
            label="Quantidade de créditos"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            variant="outlined"
            margin="dense"
            fullWidth
            autoFocus
            helperText="Use um valor negativo para corrigir um lançamento."
          />
          <TextField
            label="Descrição (opcional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            variant="outlined"
            margin="dense"
            fullWidth
            placeholder="Ex.: pacote de 5.000 créditos — fatura #123"
          />
        </Box>

        {preview !== null && (
          <Typography variant="body2" style={{ marginTop: 12 }}>
            Saldo avulso após o lançamento: <strong>{formatNumber(preview)}</strong>
          </Typography>
        )}

        <Typography variant="caption" color="textSecondary" component="p" style={{ marginTop: 12 }}>
          Os créditos avulsos só são consumidos se o plano da empresa tiver o
          controle de créditos ligado (campo "Créditos de campanha por mês" no
          cadastro de planos).
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} size="small" variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={!valid || saving}
          size="small"
          variant="contained"
          color="primary"
        >
          {parsed < 0 ? "Debitar" : "Lançar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignCreditGrantDialog;
